import type { PriceRule, SupplierQuote } from '@/services/quotes/types'
import { normalizeUomLabel } from '@/lib/uom'
import { stripDiacritics } from '@/components/forms/Typeahead'
import type { PoItemRow } from './PoCreatePageNew'

export interface QuoteRuleSuggestion {
  id: string
  rule: PriceRule
  quote?: SupplierQuote | null
  quoteType?: string
  name: string
  spec: string
  primaryText: string
  secondaryText: string
  searchText: string
}

/** Stable key used when a supplier has many priced rows for the same item name. */
export function quoteRuleNameKey(value: string): string {
  return stripDiacritics(value).normalize('NFC').trim().toLocaleLowerCase('vi').replace(/\s+/g, ' ')
}

/** Values used by a quote rule for a PO/BOM-like field. */
export function quoteRuleFieldValue(
  rule: PriceRule,
  field: string,
  canonicalKey?: string | null,
): unknown {
  const spec = (rule.spec_attributes || {}) as Record<string, unknown>
  const original = (rule.original_values || {}) as Record<string, unknown>
  const rawKey = canonicalKey || field
  const normalizedKey = stripDiacritics(rawKey).trim().toLowerCase().replace(/\s+/g, '_')
  const key = normalizedKey === 'dinh_luong' ? 'density'
    : normalizedKey === 'quy_cach' ? 'spec'
      : normalizedKey === 'loai' ? 'kind'
        : rawKey

  if (key === 'supplier_item_name' || field === 'name') {
    return rule.supplier_item_name || spec.name || spec.kind || original.name || ''
  }
  if (key === 'price' || field === 'price') return rule.unit_price
  if (key === 'uom' || field === 'uom') return rule.uom || spec.uom || original.uom || ''
  if (key === 'spec') return spec.spec || original.spec || spec.qc || spec.kt1 || (rule as any).spec_text || ''
  if (key === 'kind') return spec.kind || original.kind || ''
  if (field === 'grade') return rule.quality_grade || rule.quality_code || rule.quality_raw || spec.quality || spec.grade || ''
  if (field === 't') return spec.thickness ?? spec.thick ?? spec.t
  if (field === 'w') return spec.width ?? spec.w ?? spec.width_min ?? spec.width_max
  if (field === 'l') return spec.length ?? spec.l ?? spec.length_min ?? spec.length_max
  if (key === 'density' || field === 'density' || field === 'dinh_luong') return spec.density || original.density || spec.dinh_luong || original.dinh_luong || ''
  return spec[key] ?? spec[field] ?? original[key] ?? original[field] ?? (rule as any)[field] ?? ''
}

function quoteRuleNameCandidates(rule: PriceRule): string[] {
  const spec = (rule.spec_attributes || {}) as Record<string, unknown>
  const original = (rule.original_values || {}) as Record<string, unknown>
  return [rule.supplier_item_name, String(spec.name || ''), String(spec.kind || original.kind || '')]
}

/** Name matching shared by PO quote fields; accent-free and whitespace-tolerant. */
export function quoteRuleMatchesItemName(rule: PriceRule, itemName: string): boolean {
  const wanted = stripDiacritics(itemName).trim().replace(/\s+/g, ' ')
  if (!wanted) return false
  return quoteRuleNameCandidates(rule).some((name) => stripDiacritics(name).trim().replace(/\s+/g, ' ') === wanted)
}

/**
 * Selecting a supplier item name is only a catalog selection. It must not
 * bind one of the many priced rows that share that name; the buyer chooses
 * the dependent quote row through the PO cells (or the explicit picker).
 */
export function quoteNameSelectionPatch(
  suggestion: Pick<QuoteRuleSuggestion, 'name' | 'rule'>,
  current: Pick<PoItemRow, 'uom'>,
): Partial<PoItemRow> {
  return {
    material_id: suggestion.rule.item_id ?? undefined,
    name: suggestion.name,
    uom: current.uom,
  }
}

/** Return distinct values for a field only from rules belonging to the selected item name. */
export function quoteRuleFieldSuggestions(
  itemName: string,
  field: string,
  rules: PriceRule[],
  canonicalKey?: string | null,
): string[] {
  const values = new Set<string>()
  for (const rule of rules) {
    if (rule.status !== 'ACTIVE' || !quoteRuleMatchesItemName(rule, itemName)) continue
    const value = quoteRuleFieldValue(rule, field, canonicalKey)
    if (value !== undefined && value !== null && String(value).trim()) values.add(String(value).trim())
  }
  return [...values].sort((a, b) => a.localeCompare(b, 'vi', { numeric: true, sensitivity: 'base' }))
}

/** A dependent PO field is quote-backed only when the selected name has values for it. */
export function quoteRuleFieldIsUsed(
  itemName: string,
  field: string,
  rules: PriceRule[],
  canonicalKey?: string | null,
): boolean {
  return quoteRuleFieldSuggestions(itemName, field, rules, canonicalKey).length > 0
}

/**
 * Format a PriceRule into a rich suggestion for Typeahead dropdown.
 */
export function formatQuoteRuleSuggestion(
  rule: PriceRule,
  quote?: SupplierQuote | null,
  quoteType?: string,
): QuoteRuleSuggestion {
  const spec = (rule.spec_attributes || {}) as Record<string, unknown>
  const rawName = String(quoteRuleFieldValue(rule, 'name') || '').trim()
  const rawSpec = String(quoteRuleFieldValue(rule, 'spec') || '').trim()

  const tVal = quoteRuleFieldValue(rule, 't')
  const wVal = quoteRuleFieldValue(rule, 'w')
  const lVal = quoteRuleFieldValue(rule, 'l')

  let dimStr = ''
  if (tVal !== undefined && wVal !== undefined && lVal !== undefined) {
    dimStr = `${tVal}x${wVal}x${lVal}`
  } else if (wVal !== undefined && lVal !== undefined) {
    dimStr = `${wVal}x${lVal}`
  }

  // Primary text: item name or spec
  const primaryText = rawName || rawSpec || dimStr || 'Vật tư'

  // The name picker must not expose a price before the buyer has selected a
  // concrete quote row. Price is resolved by the server after that selection.
  // Secondary text keeps provenance/specification context only.
  const parts: string[] = []
  if (rule.uom) parts.push(rule.uom)
  if (quote?.title) {
    parts.push(quote.title)
  } else if (quote?.quote_date) {
    parts.push(`BG ${quote.quote_date}`)
  }
  if (rawSpec && rawSpec !== primaryText) {
    parts.push(`QC: ${rawSpec}`)
  } else if (dimStr && dimStr !== primaryText) {
    parts.push(`QC: ${dimStr}`)
  }

  const secondaryText = parts.join(' · ')

  // Search text: supplier_item_name, item_id, uom, price, grade, all spec_attributes values
  const searchTokens: string[] = [
    rawName,
    rawSpec,
    rule.item_id || '',
    rule.uom || '',
    String(rule.unit_price || ''),
    rule.quality_grade || '',
    rule.quality_code || '',
    rule.quality_raw || '',
    quote?.title || '',
    quote?.quote_date || '',
  ]

  for (const val of Object.values(spec)) {
    if (val !== undefined && val !== null) {
      searchTokens.push(String(val))
    }
  }

  const searchText = stripDiacritics(searchTokens.filter(Boolean).join(' '))

  return {
    id: rule.id,
    rule,
    quote,
    quoteType,
    name: rawName,
    spec: rawSpec || dimStr,
    primaryText,
    secondaryText,
    searchText,
  }
}

/**
 * Filter suggestions using accent-free multiple-word query matching.
 */
export function filterQuoteRuleSuggestion(item: QuoteRuleSuggestion, query: string): boolean {
  if (!query) return true
  const cleanQ = stripDiacritics(query).trim()
  if (!cleanQ) return true
  const words = cleanQ.split(/\s+/).filter(Boolean)
  return words.every((w) => item.searchText.includes(w))
}

/**
 * Build suggestions strictly scoped to active rules of active quotes within effective date.
 */
export function buildQuoteRuleSuggestions(
  rules: PriceRule[],
  quotesMap: Map<string, SupplierQuote> | Record<string, SupplierQuote>,
  quoteType?: string,
  todayStr?: string,
): QuoteRuleSuggestion[] {
  if (!rules || rules.length === 0) return []

  const today = todayStr || new Date().toISOString().slice(0, 10)

  const activeRules = rules.filter((r) => {
    if (r.status !== 'ACTIVE') return false
    const q =
      quotesMap instanceof Map
        ? quotesMap.get(r.quote_id)
        : (quotesMap as Record<string, SupplierQuote>)[r.quote_id]
    if (!q || q.status !== 'ACTIVE') return false
    if (q.effective_from && q.effective_from > today) return false
    if (q.effective_until && q.effective_until < today) return false
    return true
  })

  return activeRules.map((rule) => {
    const q =
      quotesMap instanceof Map
        ? quotesMap.get(rule.quote_id)
        : (quotesMap as Record<string, SupplierQuote>)[rule.quote_id]
    return formatQuoteRuleSuggestion(rule, q, quoteType)
  })
}

/**
 * Maps a chosen PriceRule into a full PO Item row patch.
 */
export function quoteRuleToPoItemPatch(
  rule: PriceRule,
  quote?: SupplierQuote | null,
  quoteType?: string,
  currentRow?: Partial<PoItemRow>,
): Partial<PoItemRow> {
  const spec = (rule.spec_attributes || {}) as Record<string, unknown>

  const patch: Partial<PoItemRow> = {
    price: Number(rule.unit_price) || 0,
    priceSource: 'auto',
    priceRuleId: rule.id,
    quoteId: rule.quote_id,
    priceWarning: null,
  }

  if (rule.item_id) {
    patch.material_id = rule.item_id
  }

  // Name
  const rawName = (rule.supplier_item_name || (spec.name as string) || (spec.kind as string) || '').trim()
  if (rawName) {
    patch.name = rawName
  }

  // Dimensions
  const tVal = spec.thickness ?? spec.thick ?? spec.t
  const wVal = spec.width ?? spec.w ?? spec.width_min ?? spec.width_max
  const lVal = spec.length ?? spec.l ?? spec.length_min ?? spec.length_max

  if (tVal !== undefined && tVal !== null && tVal !== '') patch.t = tVal as number | string
  if (wVal !== undefined && wVal !== null && wVal !== '') patch.w = wVal as number | string
  if (lVal !== undefined && lVal !== null && lVal !== '') patch.l = lVal as number | string

  // UOM
  const ruleUom = quoteRuleFieldValue(rule, 'uom')
  if (ruleUom) {
    patch.uom = String(ruleUom)
  } else if (spec.uom) {
    patch.uom = String(spec.uom)
  }

  // Spec / Desc based on quoteType
  const rawSpec = String(quoteRuleFieldValue(rule, 'spec') || '').trim()
  if (rawSpec) {
    patch.spec = rawSpec
  }

  const rawGrade = String(quoteRuleFieldValue(rule, 'grade') || '').trim()

  if (quoteType === 'van_cao_su') {
    if (rawSpec) {
      patch.spec = rawSpec
    } else if (patch.t && patch.w && patch.l) {
      patch.spec = `${patch.t}x${patch.w}x${patch.l}`
    }
    if (rawGrade) {
      patch.grade = String(rawGrade)
    }
  } else if (quoteType === 'phoi_go' || quoteType === 'van_ep' || quoteType === 'veneer') {
    // Fill name, dimensions, uom, price (already assigned above)
  } else {
    // Remaining quote types (new, accessories, etc.)
    const desc = (spec.desc as string) || (spec.description as string) || rawSpec || ''
    if (desc) {
      patch.desc = desc
    }
  }

  // Notes citation
  let quoteCite = 'Theo báo giá'
  if (quote?.title && quote?.quote_date) {
    quoteCite = `Theo báo giá ${quote.title} (${quote.quote_date})`
  } else if (quote?.title) {
    quoteCite = `Theo báo giá ${quote.title}`
  } else if (quote?.quote_date) {
    quoteCite = `Theo báo giá ngày ${quote.quote_date}`
  }

  const existingNotes = (currentRow?.notes || '').trim()
  if (!existingNotes) {
    patch.notes = quoteCite
  } else if (!existingNotes.toLowerCase().includes('theo báo giá')) {
    patch.notes = `${existingNotes} - ${quoteCite}`
  } else {
    patch.notes = existingNotes
  }

  // Calculate lineTotal
  const qtyNum = parseFloat(String(currentRow?.qty || 0).replace(/,/g, '')) || 0
  const priceNum = patch.price || 0
  const uomNorm = normalizeUomLabel(patch.uom || currentRow?.uom || '')
  const hasVol = quoteType === 'phoi_go' || uomNorm === 'm³'
  const hasArea = quoteType === 'veneer' || uomNorm === 'm²'

  const t = patch.t ?? currentRow?.t
  const w = patch.w ?? currentRow?.w
  const l = patch.l ?? currentRow?.l

  if (hasVol && t && w && l) {
    const tNum = parseFloat(String(t)) || 0
    const wNum = parseFloat(String(w)) || 0
    const lNum = parseFloat(String(l)) || 0
    const volumeM3 = (tNum * wNum * lNum * qtyNum) / 1000000000
    patch.lineTotal = Math.round(volumeM3 * priceNum)
  } else if (hasArea && w && l) {
    const wNum = parseFloat(String(w)) || 0
    const lNum = parseFloat(String(l)) || 0
    const areaM2 = (wNum * lNum * qtyNum) / 1000000
    patch.lineTotal = Math.round(areaM2 * priceNum)
  } else {
    patch.lineTotal = Math.round(qtyNum * priceNum)
  }

  return patch
}
